/**
 * LineageColumn.jsx
 *
 * Renders the narrow lineage linked list recursively, top-down (oldest ancestors
 * at the top of the page, youngest at the bottom).
 *
 * Each level draws one couple row (primary + partner cards) separated by grey
 * generation dividers. When BOTH members of a couple have recorded ancestry
 * (intermarriage), two parallel LineageColumns render above that couple —
 * one for each bloodline. Otherwise a single column continues upward.
 *
 * Parent: LineageView.jsx
 * Children: PersonCard.jsx (one per person in the couple row)
 */

import React from "react";
import PersonCard from "./PersonCard";
import { genLabel } from "./useAncestryTree";

/**
 * Recursive single-column lineage renderer.
 *
 * Renders one generation per level: a couple row (primary + partner) connected
 * by a horizontal marriage line, with a grey divider label above.
 *
 * When both members of a couple have recorded ancestry (intermarriage / C1-C2),
 * TWO parallel LineageColumns render above the couple — one for each bloodline.
 * Otherwise a single column continues upward following the primary's line.
 *
 * Recursion terminates when gen is null (no further ancestors).
 */
function LineageColumn({ gen, highlightedPath, onHover, onLeave, onClick }) {
  if (!gen) return null;

  const primaryHighlighted = highlightedPath.has(String(gen.primary.id));
  const partnerHighlighted = gen.partner
    ? highlightedPath.has(String(gen.partner.id))
    : false;
  const connectorHighlighted = primaryHighlighted || partnerHighlighted;

  // IDs to suppress in spouse tags (they're already shown as full cards)
  const primarySuppressIds = [
    ...(gen.partner ? [gen.partner.id] : []),
    ...gen.extraSpouses.map((sp) => sp.id),
  ];
  const partnerSuppressIds = [gen.primary.id];

  // Marriage line highlighted if both primary and partner are on the path
  const marriageHighlighted = primaryHighlighted && partnerHighlighted;

  return (
    <div className="lineage-column">
      {/* What's above this generation */}
      {gen.partnerAbove ? (
        // BRANCH: two parallel columns above this couple
        <div className="lineage-branch-pair">
          <LineageColumn
            gen={gen.above}
            highlightedPath={highlightedPath}
            onHover={onHover}
            onLeave={onLeave}
            onClick={onClick}
          />
          <LineageColumn
            gen={gen.partnerAbove}
            highlightedPath={highlightedPath}
            onHover={onHover}
            onLeave={onLeave}
            onClick={onClick}
          />
        </div>
      ) : (
        // Normal: single column continues upward
        <LineageColumn
          gen={gen.above}
          highlightedPath={highlightedPath}
          onHover={onHover}
          onLeave={onLeave}
          onClick={onClick}
        />
      )}

      {/* Grey generation divider with label */}
      <div className="lineage-gen-divider">
        <div className="lineage-gen-divider-line" />
        <span className="lineage-gen-divider-label">{genLabel(gen.depth)}</span>
        <div className="lineage-gen-divider-line" />
      </div>

      {/* Couple row: primary card + marriage line + partner card + extra spouses */}
      <div className="lineage-couple-row">
        <PersonCard
          node={gen.primary}
          isHighlighted={primaryHighlighted}
          suppressSpouseIds={primarySuppressIds}
          onMouseEnter={() => onHover(gen.primary.id)}
          onMouseLeave={onLeave}
          onClick={() => onClick(gen.primary.id)}
        />
        {gen.partner && (
          <div
            className={`lineage-marriage-line${marriageHighlighted ? " lineage-marriage-highlighted" : ""}`}
          />
        )}
        {gen.partner && (
          <PersonCard
            node={gen.partner}
            isHighlighted={partnerHighlighted}
            suppressSpouseIds={partnerSuppressIds}
            onMouseEnter={() => onHover(gen.partner.id)}
            onMouseLeave={onLeave}
            onClick={() => onClick(gen.partner.id)}
          />
        )}
        {gen.extraSpouses.map((sp) => (
          <React.Fragment key={sp.id}>
            <div className="lineage-marriage-line" />
            <PersonCard
              node={{
                id: sp.id,
                data: sp.data,
                spouses: [],
                duplicate: false,
              }}
              isHighlighted={highlightedPath.has(String(sp.id))}
              onMouseEnter={() => onHover(sp.id)}
              onMouseLeave={onLeave}
              onClick={() => onClick(sp.id)}
            />
          </React.Fragment>
        ))}
      </div>

      {/* Vertical connector down to the next generation */}
      <div
        className={`lineage-vertical-connector${connectorHighlighted ? " lineage-connector-highlighted" : ""}`}
      />
    </div>
  );
}

export default React.memo(LineageColumn);
